import React, { useState, useEffect } from 'react';
import api from '../../api';
import AdminLayout from '../../components/AdminLayout';

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingModal, setTrackingModal] = useState(null); // { id, currentStatus }
  const [trackingForm, setTrackingForm] = useState({ status: '', tracking_number: '', tracking_carrier: 'USPS' });
  const [refundAmount, setRefundAmount] = useState('');
  const [refunding, setRefunding] = useState(false);
  const [refundResult, setRefundResult] = useState(null);

  const loadOrders = () => {
    const params = filter ? `?status=${filter}` : '';
    api.get(`/orders${params}`).then(r => { setOrders(r.data.orders); setLoading(false); });
  };

  useEffect(() => { loadOrders(); }, [filter]);

  const openStatusUpdate = (order, newStatus) => {
    if (newStatus === 'shipped') {
      setTrackingModal(order);
      setTrackingForm({ status: 'shipped', tracking_number: order.tracking_number || '', tracking_carrier: order.tracking_carrier || 'USPS' });
    } else {
      updateStatus(order.id, newStatus, '', '');
    }
  };

  const updateStatus = async (id, status, tracking_number, tracking_carrier) => {
    await api.put(`/orders/${id}/status`, { status, tracking_number, tracking_carrier });
    setTrackingModal(null);
    loadOrders();
    if (selectedOrder?.id === id) {
      const r = await api.get(`/orders/${id}`);
      setSelectedOrder(r.data);
    }
  };

  const viewOrder = async (id) => {
    const r = await api.get(`/orders/${id}`);
    setSelectedOrder(r.data);
    setRefundAmount('');
    setRefundResult(null);
  };

  const handleRefund = async () => {
    if (!window.confirm(`Issue refund of ${refundAmount ? `$${refundAmount}` : 'full amount'} for Order #${selectedOrder.id}?`)) return;
    setRefunding(true);
    setRefundResult(null);
    try {
      const body = { order_id: selectedOrder.id };
      if (refundAmount) body.amount = refundAmount;
      const r = await api.post('/payments/refund', body);
      setRefundResult({ success: true, msg: `✅ Refund of $${r.data.amount.toFixed(2)} ${r.data.currency} issued (${r.data.status})` });
    } catch (e) {
      setRefundResult({ success: false, msg: `❌ ${e.response?.data?.error || 'Refund failed'}` });
    } finally {
      setRefunding(false);
    }
  };

  const statuses = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

  return (
    <AdminLayout title="Orders">
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button className={`btn btn-sm ${!filter ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilter('')}>All</button>
        {statuses.map(s => (
          <button key={s} className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilter(s)}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Order #</th><th>Customer</th><th>Total</th><th>Status</th><th>Tracking</th><th>Date</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id}>
                  <td>#{o.id}</td>
                  <td>{o.customer_name || 'Guest'}<div style={{ fontSize: 12, color: '#999' }}>{o.customer_email}</div></td>
                  <td>${o.total.toFixed(2)}</td>
                  <td><span className={`badge badge-${o.status}`}>{o.status}</span></td>
                  <td style={{ fontSize: 12 }}>{o.tracking_number ? `${o.tracking_carrier || ''} ${o.tracking_number}` : '-'}</td>
                  <td style={{ fontSize: 13 }}>{new Date(o.created_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm btn-outline" onClick={() => viewOrder(o.id)}>View</button>
                      {o.status !== 'delivered' && o.status !== 'cancelled' && (
                        <select
                          style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #ddd', borderRadius: 4 }}
                          value="" onChange={e => openStatusUpdate(o, e.target.value)}
                        >
                          <option value="">Update...</option>
                          {statuses.filter(s => s !== o.status).map(s => (
                            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#999' }}>No orders found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Tracking number modal (shown when marking as Shipped) */}
      {trackingModal && (
        <div className="modal-overlay" onClick={() => setTrackingModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h2>Mark as Shipped — Order #{trackingModal.id}</h2>
            <div className="form-group">
              <label>Carrier</label>
              <select value={trackingForm.tracking_carrier} onChange={e => setTrackingForm({ ...trackingForm, tracking_carrier: e.target.value })}>
                <option value="USPS">USPS</option>
                <option value="FedEx">FedEx</option>
                <option value="UPS">UPS</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Tracking Number</label>
              <input
                value={trackingForm.tracking_number}
                onChange={e => setTrackingForm({ ...trackingForm, tracking_number: e.target.value })}
                placeholder="e.g. 9400111899223397990011"
              />
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button className="btn btn-primary" onClick={() => updateStatus(trackingModal.id, 'shipped', trackingForm.tracking_number, trackingForm.tracking_carrier)}>
                Mark Shipped
              </button>
              <button className="btn btn-outline" onClick={() => setTrackingModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Order detail modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Order #{selectedOrder.id}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div>
                <strong>Customer:</strong> {selectedOrder.customer_name}<br />
                <strong>Email:</strong> {selectedOrder.customer_email}<br />
                <strong>Date:</strong> {new Date(selectedOrder.created_at).toLocaleString()}
              </div>
              <div>
                <strong>Status:</strong> <span className={`badge badge-${selectedOrder.status}`}>{selectedOrder.status}</span><br />
                {selectedOrder.tracking_number && (<><strong>Tracking:</strong> {selectedOrder.tracking_carrier} — {selectedOrder.tracking_number}<br /></>)}
                <strong>Subtotal:</strong> ${selectedOrder.subtotal?.toFixed(2)}<br />
                <strong>Tax:</strong> ${selectedOrder.tax?.toFixed(2)}<br />
                <strong>Shipping:</strong> ${selectedOrder.shipping?.toFixed(2)}<br />
                <strong>Total:</strong> <strong>${selectedOrder.total?.toFixed(2)}</strong>
              </div>
            </div>
            <h3 style={{ marginBottom: 8 }}>Items</h3>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Product</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead>
                <tbody>
                  {selectedOrder.items?.map(i => (
                    <tr key={i.id}>
                      <td>{i.product_name}</td><td>{i.quantity}</td>
                      <td>${i.price.toFixed(2)}</td><td>${i.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {selectedOrder.shipping_address && (() => {
              const a = JSON.parse(selectedOrder.shipping_address);
              return (
                <div style={{ marginTop: 20, padding: '14px 16px', background: '#f9fafb', borderRadius: 8 }}>
                  <strong>📦 Ship To:</strong>
                  <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.8 }}>
                    {a.first_name} {a.last_name}<br />
                    {a.address}{a.address2 ? `, ${a.address2}` : ''}<br />
                    {a.city}, {a.state} {a.zip}<br />
                    {a.phone && <span>📞 {a.phone}</span>}
                  </div>
                </div>
              );
            })()}
            {selectedOrder.notes && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#fffbeb', borderRadius: 8, fontSize: 14 }}>
                <strong>📝 Notes:</strong> {selectedOrder.notes}
              </div>
            )}

            {/* Stripe Refund */}
            {(selectedOrder.notes || '').includes('stripe:') && (
              <div style={{ marginTop: 20, padding: '16px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                <strong>💳 Issue Stripe Refund</strong>
                <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                  <input
                    type="number" step="0.01" min="0.01"
                    placeholder={`Full refund ($${selectedOrder.total?.toFixed(2)})`}
                    value={refundAmount}
                    onChange={e => setRefundAmount(e.target.value)}
                    style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1fae5', borderRadius: 6, fontSize: 13 }}
                  />
                  <button className="btn btn-sm btn-primary" onClick={handleRefund} disabled={refunding}>
                    {refunding ? 'Processing…' : 'Refund'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Leave amount blank for full refund</div>
                {refundResult && (
                  <div style={{ marginTop: 8, fontSize: 13, color: refundResult.success ? '#065f46' : '#991b1b' }}>
                    {refundResult.msg}
                  </div>
                )}
              </div>
            )}
            <button className="btn btn-outline" style={{ marginTop: 16 }} onClick={() => setSelectedOrder(null)}>Close</button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

